import React from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import clsx from 'clsx';

import { ChipLabel, FILTER_CHIP_CLASS, FILTER_PANEL_CLASS } from './filter-chips';

/** A filter chip that sets a folder path, its names entered as tags. */
export function FolderPathChip(props: { value: string[]; onChange: (value: string[]) => void }) {
  const { value, onChange } = props;
  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <PopoverButton
            className={FILTER_CHIP_CLASS}
            data-active={value.length > 0 ? '' : undefined}
          >
            <ChipLabel label="Folder" value={value.length > 0 ? value.join(' / ') : undefined} />
          </PopoverButton>
          <PopoverPanel anchor="bottom start" className={clsx(FILTER_PANEL_CLASS, 'w-80 p-2.5')}>
            <FolderPathEditor
              initial={value}
              onApply={(path) => {
                onChange(path);
                close();
              }}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}

/** Splits typed text at "/" into finished folder names and the name still being typed. */
export function splitFolderText(text: string): { names: string[]; rest: string } {
  const parts = text.split('/');
  const rest = parts.pop() ?? '';
  return { names: parts.map((p) => p.trim()).filter(Boolean), rest: rest.trimStart() };
}

/**
 * Edits a folder path as tags: "/" or leaving the box finishes the name being
 * typed, Backspace in an empty box erases the last name, and Enter or Apply
 * sets the path with whatever is still typed as its last name. Closing the
 * popover any other way discards the edit.
 */
function FolderPathEditor(props: { initial: string[]; onApply: (path: string[]) => void }) {
  const [names, setNames] = React.useState(props.initial);
  const [text, setText] = React.useState('');
  const finish = () => {
    const name = text.trim();
    if (name) setNames((previous) => [...previous, name]);
    setText('');
  };
  const apply = () => {
    const name = text.trim();
    props.onApply(name ? [...names, name] : names);
  };
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        Folder path
      </div>
      <div
        className={clsx(
          'flex min-h-8 flex-wrap items-center gap-x-1 gap-y-1 rounded-md border border-neutral-950/10 px-2 py-1',
          'focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20',
          'dark:border-white/10 dark:bg-white/5'
        )}
      >
        {names.map((name, i) => (
          <React.Fragment key={`${i}:${name}`}>
            {i > 0 && <PathSeparator />}
            <span
              data-testid="folder-path-part"
              className="inline-flex items-center rounded bg-primary-100 px-2 py-0.5 text-xs text-primary-900 dark:bg-primary-500/20 dark:text-primary-200"
            >
              {name}
            </span>
          </React.Fragment>
        ))}
        {names.length > 0 && <PathSeparator />}
        <input
          type="text"
          autoFocus
          value={text}
          aria-label="Folder path"
          placeholder={names.length > 0 ? 'Subfolder' : 'Folder'}
          onChange={(e) => {
            const { names: finished, rest } = splitFolderText(e.currentTarget.value);
            if (finished.length > 0) setNames((previous) => [...previous, ...finished]);
            setText(rest);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && text === '' && names.length > 0) {
              e.preventDefault();
              setNames((previous) => previous.slice(0, -1));
            } else if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              apply();
            }
          }}
          onBlur={finish}
          className="min-w-20 flex-1 border-0 bg-transparent p-0 text-sm text-neutral-950 placeholder:text-neutral-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-neutral-500"
        />
      </div>
      <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
        Type / between folder names. Files in subfolders are included.
      </p>
      <div className="mt-2.5 flex items-center justify-end gap-3">
        {(names.length > 0 || text) && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setNames([]);
              setText('');
            }}
            className="text-xs font-medium text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
          >
            Clear
          </button>
        )}
        <button
          type="button"
          // Applying reads what is still typed itself, so the box must not
          // finish it on blur first and apply a stale path.
          onMouseDown={(e) => e.preventDefault()}
          onClick={apply}
          className="rounded-md bg-sky-600 px-2.5 py-1 text-xs font-medium text-white shadow-sm hover:bg-sky-500"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

function PathSeparator() {
  return (
    <span className="text-xs text-neutral-400 dark:text-neutral-500" aria-hidden="true">
      /
    </span>
  );
}
